import { styled } from '@mui/material/styles';
import { Box, Button, Typography, Switch, Slider, TextField, Select, Checkbox } from '@mui/material';

// ============================================
// Main Container
// ============================================
export const PageContainer = styled(Box)({
  background: '#fff',
  borderRadius: '10px',
  padding: '32px 40px',
  minHeight: 'calc(100vh - 200px)',
});

export const PageTitle = styled(Typography)({
  fontSize: '24px',
  fontWeight: 600,
  color: '#111',
  marginBottom: '8px',
});

export const BreadCrumb = styled(Typography)({
  fontSize: '14px',
  color: '#666',
  marginBottom: '24px',
});

// ============================================
// Header Tabs
// ============================================
export const HeaderTabsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '24px',
});

export const TabGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
});

export const Tab = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  padding: '12px 24px',
  borderRadius: '24px',
  fontSize: '14px',
  fontWeight: 500,
  minWidth: '100px',
  textTransform: 'none',
  border: isActive ? '1px solid #0F7F85' : '1px solid #E0E0E0',
  backgroundColor: isActive ? '#0F7F85' : '#fff',
  color: isActive ? '#fff' : '#666',
  '&:hover': {
    backgroundColor: isActive ? '#0D6A6F' : '#F5F5F5',
    border: isActive ? '1px solid #0D6A6F' : '1px solid #E0E0E0',
  },
}));

export const CenterTabGroup = styled(Box)({
  display: 'flex',
  backgroundColor: '#F0F0F0',
  borderRadius: '25px',
  padding: '4px',
  marginLeft: 'auto',
  marginRight: 'auto',
});

export const CenterTab = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 32px',
  borderRadius: '20px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: isActive ? '#0F7F85' : 'transparent',
  color: isActive ? '#fff' : '#666',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: isActive ? '#0D6A6F' : '#E0E0E0',
  },
}));

// ============================================
// Filter Tabs
// ============================================
export const FilterTabsContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '420px',
  marginBottom: '20px',
});

export const FilterTabGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
});

export const FilterTab = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  padding: '12px 10px',
  borderRadius: '12px',
  height: '40px',
  width: '80px',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  backgroundColor: isActive ? '#0F7F85' : '#fff',
  color: isActive ? '#fff' : '#A6A6A6',
  border: isActive ? 'none' : '1px solid #E6E6E6',
  '&:hover': {
    backgroundColor: isActive ? '#0D6A6F' : '#F5F5F5',
  },
}));

export const EntryTimeSettingButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  height: '40px',
  borderRadius: '8px',
  border: '1px solid #0F7F85',
  backgroundColor: '#fff',
  color: '#0F7F85',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#E7F2F3',
    border: '1px solid #0F7F85',
  },
});

// ============================================
// Content Layout
// ============================================
export const ContentContainer = styled(Box)({
  display: 'flex',
  gap: '24px',
});

export const LeftPanel = styled(Box)({
  flex: '0 0 420px',
  maxWidth: '420px',
});

export const RightPanel = styled(Box)({
  flex: 1,
  minWidth: 0,
  backgroundColor: '#F8F9FA',
  borderRadius: '12px',
  padding: '24px',
  overflow: 'auto',
});

// ============================================
// Zone Section
// ============================================
export const ZoneSection = styled(Box)({
  marginBottom: '24px',
});

export const ZoneTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
});

export const ZoneTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#111',
});

export const EditIcon = styled(Box)({
  cursor: 'pointer',
  color: '#666',
  display: 'flex',
  alignItems: 'center',
});

// ============================================
// Room Card
// ============================================
export const RoomCard = styled(Box, {
  shouldForwardProp: prop => !['isSelected', 'isEnabled'].includes(prop),
})(({ isSelected, isEnabled }) => ({
  backgroundColor: isSelected && isEnabled ? '#E7F2F3' : '#fff',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
  border: isSelected && isEnabled ? '1px solid #0F7F85' : '1px solid #E0E0E0',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  '&:hover': {
    borderColor: isEnabled ? '#0F7F85' : '#E0E0E0',
  },
}));

export const RoomCardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const RoomName = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

export const RoomNameText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isEnabled',
})(({ isEnabled }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: isEnabled ? '#333' : '#999',
}));

export const RoomStatusBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'status',
})(({ status }) => ({
  padding: '4px 12px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 500,
  minWidth: '56px',
  textAlign: 'center',
  backgroundColor: status === 'active' ? '#0F7F85' : '#E0E0E0',
  color: status === 'active' ? '#fff' : '#999',
}));

export const RoomCardControls = styled(Box)({
  display: 'flex',
  gap: '8px',
  width: '100%',
  overflow: 'hidden',
});

// IoT 제어 아이템 (온/냉방, 조명, 일시개방 등)
export const IoTControlItem = styled(Box, {
  shouldForwardProp: prop => !['isActive', 'isEnabled'].includes(prop),
})(({ isActive, isEnabled = true }) => ({
  flex: '1 1 0',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '12px 10px',
  height: '50px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  border: isActive && isEnabled ? '1px solid #0F7F85' : '1px solid #E0E0E0',
  cursor: isEnabled ? 'pointer' : 'default',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: isEnabled ? '#0F7F85' : '#E0E0E0',
  },
}));

export const IoTControlLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
  minWidth: 0,
});

export const IoTControlText = styled(Typography, {
  shouldForwardProp: prop => !['isActive', 'isEnabled'].includes(prop),
})(({ isActive, isEnabled = true }) => ({
  fontSize: '14px',
  fontWeight: isActive && isEnabled ? 600 : 500,
  color: isActive && isEnabled ? '#0F7F85' : '#999',
  whiteSpace: 'nowrap',
}));

export const IoTControlRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const ControlButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '8px 12px',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: isActive ? '#0F7F85' : '#F5F5F5',
  color: isActive ? '#fff' : '#666',
  border: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: isActive ? '#0D6A6F' : '#E0E0E0',
  },
}));

export const RefreshButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  backgroundColor: '#F5F5F5',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#E0E0E0',
  },
});

// ============================================
// Right Panel - Room Detail
// ============================================
export const DetailHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
});

export const DetailTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#111',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const StatusToggle = styled(Box)({
  display: 'flex',
  gap: '8px',
});

export const StatusButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  padding: '6px 16px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: isActive ? '#0F7F85' : '#E0E0E0',
  color: isActive ? '#fff' : '#666',
  border: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: isActive ? '#0D6A6F' : '#D0D0D0',
  },
}));

// ============================================
// Setting Section
// ============================================
export const SettingSection = styled(Box)({
  marginBottom: '20px',
});

export const SettingRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  marginBottom: '12px',
});

export const SettingLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
});

export const StyledSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#0F7F85',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#0F7F85',
  },
});

// ============================================
// Temperature Control
// ============================================
export const TemperatureControl = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const TemperatureInput = styled(TextField)({
  width: '80px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    height: '40px',
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'center',
    fontSize: '14px',
  },
});

export const TemperatureLabel = styled(Typography)({
  fontSize: '14px',
  color: '#666',
});

// ============================================
// Fan Speed Control
// ============================================
export const FanSpeedControl = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: '12px',
});

export const FanSpeedButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  padding: '8px 16px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: isActive ? '#0F7F85' : '#F5F5F5',
  color: isActive ? '#fff' : '#666',
  border: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: isActive ? '#0D6A6F' : '#E0E0E0',
  },
}));

// ============================================
// Mode Control
// ============================================
export const ModeControl = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: '12px',
});

export const ModeButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  padding: '8px 16px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: isActive ? '#0F7F85' : '#F5F5F5',
  color: isActive ? '#fff' : '#666',
  border: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: isActive ? '#0D6A6F' : '#E0E0E0',
  },
}));

// ============================================
// Lighting Control
// ============================================
export const LightingSliderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flex: 1,
});

export const LightingLabel = styled(Typography)({
  fontSize: '14px',
  color: '#333',
  minWidth: '60px',
});

export const StyledSlider = styled(Slider)({
  color: '#0F7F85',
  '& .MuiSlider-thumb': {
    width: 20,
    height: 20,
    backgroundColor: '#fff',
    border: '2px solid #0F7F85',
  },
  '& .MuiSlider-track': {
    height: 6,
  },
  '& .MuiSlider-rail': {
    height: 6,
    backgroundColor: '#E0E0E0',
  },
});

export const SliderIcon = styled(Box)({
  color: '#666',
  display: 'flex',
  alignItems: 'center',
});

// ============================================
// Air Quality
// ============================================
export const AirQualityContainer = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '8px',
  padding: '16px',
});

export const AirQualityStatus = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
});

export const AirQualityBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'status',
})(({ status }) => ({
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '13px',
  fontWeight: 500,
  backgroundColor: status === 'good' ? '#E6F7F8' : status === 'moderate' ? '#FFF3E6' : '#FFE6E6',
  color: status === 'good' ? '#0F7F85' : status === 'moderate' ? '#FF9500' : '#FF3B30',
}));

export const AirQualityValues = styled(Box)({
  display: 'flex',
  gap: '24px',
});

export const AirQualityItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const AirQualityLabel = styled(Typography)({
  fontSize: '13px',
  color: '#666',
});

export const AirQualityValue = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#333',
});

// ============================================
// Monitor & Ad Board
// ============================================
export const MonitorGrid = styled(Box)({
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
});

export const MonitorItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  border: isActive ? '2px solid #0F7F85' : '1px solid #E0E0E0',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#0F7F85',
  },
}));

export const MonitorLabel = styled(Typography)({
  fontSize: '13px',
  color: '#333',
});

// ============================================
// Lighting Grid
// ============================================
export const LightingGrid = styled(Box)({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
});

export const LightingItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  border: isActive ? '2px solid #0F7F85' : '1px solid #E0E0E0',
  cursor: 'pointer',
  minWidth: '60px',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#0F7F85',
  },
}));

export const LightingItemIcon = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  color: isActive ? '#0F7F85' : '#999',
}));

export const LightingItemLabel = styled(Typography)({
  fontSize: '12px',
  color: '#666',
});

// ============================================
// Schedule Section
// ============================================
export const ScheduleContainer = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '8px',
  padding: '16px',
  marginTop: '12px',
});

export const ScheduleHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
});

export const ScheduleTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const DaySelector = styled(Box)({
  display: 'flex',
  gap: '4px',
  marginBottom: '12px',
});

export const DayButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  minWidth: '36px',
  height: '36px',
  padding: 0,
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: isActive ? '#0F7F85' : '#F5F5F5',
  color: isActive ? '#fff' : '#666',
  border: 'none',
  '&:hover': {
    backgroundColor: isActive ? '#0D6A6F' : '#E0E0E0',
  },
}));

export const TimeSelector = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const TimeSelect = styled(Select)({
  minWidth: '70px',
  height: '36px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '6px',
  },
  '& .MuiSelect-select': {
    padding: '8px 12px',
    fontSize: '13px',
  },
});

export const TimeSeparator = styled(Typography)({
  fontSize: '14px',
  color: '#666',
});

// ============================================
// Checkbox Group
// ============================================
export const CheckboxGroup = styled(Box)({
  marginTop: '16px',
});

export const CheckboxGroupTitle = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#333',
  marginBottom: '8px',
});

export const CheckboxGrid = styled(Box)({
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
});

export const StyledCheckbox = styled(Checkbox)({
  color: '#E0E0E0',
  '&.Mui-checked': {
    color: '#0F7F85',
  },
});

// ============================================
// Add Button
// ============================================
export const AddButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: '#666',
  fontSize: '13px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#0F7F85',
  },
});

// ============================================
// Two Column Layout
// ============================================
export const TwoColumnLayout = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
});

export const SettingColumn = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '8px',
  padding: '16px',
});

export const SettingColumnTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  marginBottom: '12px',
});

export const CollapseButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
  color: '#666',
  cursor: 'pointer',
  fontSize: '13px',
  '&:hover': {
    color: '#0F7F85',
  },
});

// ============================================
// Right Panel Header
// ============================================
export const RightPanelHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
});

export const RightPanelTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const RightPanelTitleText = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#333',
});

export const RightPanelBadge = styled(Box)({
  padding: '4px 16px',
  borderRadius: '4px',
  fontSize: '16px',
  fontWeight: 500,
  backgroundColor: '#0F7F85',
  color: '#fff',
});

// ============================================
// Setting Card
// ============================================
export const SettingCard = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '16px',
});

export const SettingCardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
});

export const SettingCardTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#333',
});

export const SettingCardContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

// ============================================
// Temperature Control (new)
// ============================================
export const TemperatureBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px 10px',
  gap: '8px',
  width: '100%',
  background: '#F2F2F2',
  borderRadius: '20px',
});

export const TemperatureButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'variant',
})(({ variant }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  backgroundColor: variant === 'minus' ? '#E0E0E0' : variant === 'plus' ? '#0F7F85' : '#fff',
  color: variant === 'plus' ? '#fff' : '#333',
  border: '1px solid #E0E0E0',
  fontSize: '18px',
  fontWeight: 500,
  '&:hover': {
    opacity: 0.8,
  },
}));

export const TemperatureDisplay = styled(Box)({
  minWidth: '60px',
  textAlign: 'center',
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
});

export const CurrentTemperature = styled(Typography)({
  fontSize: '14px',
  color: '#666',
  marginLeft: '16px',
});

// ============================================
// Control Item (모니터, 광고판, 조명)
// ============================================
export const ControlItemGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
});

export const ControlItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  border: isActive ? '1px solid #0F7F85' : '1px solid #E0E0E0',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#0F7F85',
  },
}));

export const ControlItemLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#333',
  whiteSpace: 'nowrap',
});

// ============================================
// Lighting Item Grid (조명 그리드)
// ============================================
export const LightingItemGrid = styled(Box)({
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
});

export const LightingItemBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive' && prop !== 'isDisabled',
})(({ isActive, isDisabled }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: '72px',
  height: '100px',
  borderRadius: '8px',
  backgroundColor: isDisabled ? '#F5F5F5' : '#fff',
  border: isDisabled ? '1px dashed #BDBDBD' : isActive ? '1px solid #0F7F85' : '1px solid #E0E0E0',
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  opacity: isDisabled ? 0.7 : 1,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: isDisabled ? '#BDBDBD' : '#0F7F85',
  },
}));

export const LightingItemNumber = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isActive' && prop !== 'isDisabled',
})(({ isActive, isDisabled }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: isDisabled ? '#9E9E9E' : isActive ? '#0F7F85' : '#666',
  textAlign: 'center',
  lineHeight: 1.3,
  textDecoration: isDisabled ? 'line-through' : 'none',
}));

export const LightingItemDisabledBadge = styled(Box)({
  position: 'absolute',
  top: '4px',
  right: '4px',
  padding: '2px 6px',
  borderRadius: '4px',
  backgroundColor: '#9E9E9E',
  color: '#fff',
  fontSize: '10px',
  fontWeight: 600,
  lineHeight: 1,
});

// ============================================
// Air Quality Card
// ============================================
export const AirQualityCard = styled(Box)({
  backgroundColor: '#E7F2F3',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '16px',
});

export const AirQualityHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
});

export const AirQualityIcon = styled(Box)({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: '#0F7F85',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
});

export const AirQualityText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#0F7F85',
});

export const AirQualityInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const AirQualityInfoRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const AirQualityInfoLabel = styled(Typography)({
  fontSize: '13px',
  color: '#666',
});

export const AirQualityInfoValue = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#333',
});

// ============================================
// Schedule Card
// ============================================
export const ScheduleCard = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '12px',
  padding: '16px',
  border: '1px solid #E0E0E0',
});

export const ScheduleCardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
});

export const ScheduleCardTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const ScheduleCardTitleText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#333',
});

// ============================================
// Open/Close Button
// ============================================
export const OpenCloseToggle = styled(Box)({
  display: 'flex',
  backgroundColor: '#F2F2F2',
  borderRadius: '12px',
  width: '186px',
});

export const OpenCloseButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  flex: 1,
  padding: '10px',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: isActive ? 600 : 500,
  cursor: 'pointer',
  textAlign: 'center',
  backgroundColor: isActive ? '#4ab3bc' : 'transparent',
  color: isActive ? '#fff' : '#666',
  border: isActive ? '2px solid #F2F2F2' : '2px solid transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: isActive ? '#4ab3bc' : 'rgba(255, 255, 255, 0.5)',
  },
}));

// 출입문 설정 카드 (Figma 디자인)
export const DoorSettingCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.1)',
  width: '100%',
});

export const DoorSettingInner = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '276px',
});

export const DoorSettingTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
  whiteSpace: 'nowrap',
});

export const ReservationHistoryButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  height: '37px',
  padding: '12px 16px',
  backgroundColor: '#fff',
  border: '1px solid #4ab3bc',
  borderRadius: '8px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f5fafa',
  },
});

export const ReservationHistoryText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#4ab3bc',
  whiteSpace: 'nowrap',
});

// ============================================
// Section Label
// ============================================
export const SectionLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#666',
  marginBottom: '8px',
});

export const SectionLabelRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
});

// ============================================
// Button Group
// ============================================
export const ButtonGroup = styled(Box)({
  display: 'flex',
  backgroundColor: '#F2F2F2',
  borderRadius: '8px',
  padding: '4px',
});

export const GroupButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  flex: 1,
  padding: '8px 16px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'center',
  backgroundColor: isActive ? '#fff' : 'transparent',
  color: isActive ? '#0F7F85' : '#666',
  border: isActive ? '1px solid #0F7F85' : '1px solid transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: isActive ? '#fff' : 'rgba(255, 255, 255, 0.5)',
  },
}));

// ============================================
// Add Schedule Button
// ============================================
export const AddScheduleButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  padding: '12px',
  color: '#666',
  fontSize: '13px',
  cursor: 'pointer',
  '&:hover': {
    color: '#0F7F85',
  },
});

// ============================================
// New Schedule Design (Figma)
// ============================================

// 시스템 카드 (회색 배경)
export const ScheduleSystemCard = styled(Box)({
  backgroundColor: '#F2F2F2',
  borderRadius: '8px',
  padding: '24px',
  marginBottom: '16px',
});

// 시스템 헤더 (이름 + 편집 + 전원 아이콘)
export const ScheduleSystemHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '16px',
});

export const ScheduleSystemTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const ScheduleSystemName = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#000',
});

// 요일 선택 행
export const ScheduleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
});

export const ScheduleRowLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  width: '98px',
  flexShrink: 0,
});

// 요일 버튼 그룹
export const ScheduleDayGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
});

export const ScheduleDayBtn = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  width: '56px',
  padding: '12px 16px',
  borderRadius: '12px',
  fontSize: '14px',
  fontWeight: 600,
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: isActive ? '#0F7F85' : '#DCDCDC',
  color: isActive ? '#fff' : '#333',
  transition: 'all 0.2s ease',
  '&:hover': {
    opacity: 0.8,
  },
}));

// 시간 선택기 (새 디자인)
export const ScheduleTimeGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '440px',
});

export const ScheduleTimeBlock = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flex: 1,
});

export const ScheduleTimeInput = styled(Select)({
  flex: 1,
  height: '48px',
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D9D9D9',
    borderRadius: '8px',
  },
  '& .MuiSelect-select': {
    padding: '12px',
    fontSize: '16px',
    color: '#404040',
  },
});

export const ScheduleTimeSeparator = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  width: '12px',
  textAlign: 'center',
});

// 접기/펼치기 버튼
export const ScheduleCollapseBtn = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '4px',
  padding: '8px 0',
  color: '#8C8C8C',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    color: '#0F7F85',
  },
});

// 설정 그룹 (모니터, 광고판, 조명)
export const ScheduleSettingSection = styled(Box)({
  marginTop: '24px',
});

export const ScheduleSettingTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  marginBottom: '12px',
});

export const ScheduleSettingBox = styled(Box)({
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
});

export const ScheduleCheckboxRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const ScheduleCheckboxItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
});

export const ScheduleCheckboxLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
});

// 조명 체크박스 그리드 (3열)
export const ScheduleLightingGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px 0',
});

export const ScheduleLightingItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '33.33%',
});

// 추가하기 카드
export const AddScheduleCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '16px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#0F7F85',
  },
});

export const AddScheduleText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#A6A6A6',
});

// 전원 아이콘
export const PowerIcon = styled(Box)({
  width: '26px',
  height: '26px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#0F7F85',
});

// ============================================
// Schedule Design (Figma) - 새로운 컴포넌트
// ============================================

// 기간 입력 필드
export const ScheduleDateInput = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  cursor: 'pointer',
  '& p': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#A6A6A6',
  },
});

// 만료없음 체크박스 래퍼
export const ScheduleNoExpireWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// 시간 선택 드롭다운 (새 디자인)
export const ScheduleTimeSelect = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  height: '48px',
  '& p': {
    flex: 1,
    fontSize: '16px',
    fontWeight: 500,
    color: '#404040',
  },
});

// 설정 섹션 컨테이너 (모니터/광고판/조명)
export const ScheduleSettingContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginTop: '24px',
});

// 설정 섹션 제목
export const ScheduleSettingSectionTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
});

// 설정 체크박스 그리드 박스
export const ScheduleSettingCheckboxBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
});

// 체크박스 아이템
export const ScheduleCheckboxItemNew = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// 조명 설정 그리드 (2열)
export const ScheduleLightingCheckboxGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px 0',
  padding: '16px',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
});

// 조명 체크박스 아이템 (50% 너비)
export const ScheduleLightingCheckboxItem = styled(Box)({
  width: '50%',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// 접기 버튼
export const ScheduleCollapseBtnNew = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '4px',
  cursor: 'pointer',
  marginTop: '8px',
  '& p': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#8C8C8C',
  },
});

// 스케줄 폼 라벨 (64px 너비)
export const ScheduleFormLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  width: '64px',
  flexShrink: 0,
});

// ============================================
// New Schedule Design (2024.03.11)
// ============================================

// 요일별 예약 설정 전체 컨테이너 (흰색 배경 + 그림자)
export const ScheduleSettingWrapper = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.25)',
  overflow: 'hidden',
});

// 요일별 예약 설정 헤더
export const ScheduleSettingHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
});

export const ScheduleSettingHeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
});

// 시스템 카드 2열 레이아웃 (wrap으로 여러 줄 지원)
export const ScheduleSystemCardGrid = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '16px',
});

// 시스템 카드 (1열 레이아웃)
export const ScheduleSystemCardNew = styled(Box)({
  width: '100%',
  backgroundColor: '#F2F2F2',
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

// 시스템 카드 상단 영역 (요일, 시간 등)
export const ScheduleSystemTopSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

// 시스템 헤더 (새 디자인) - 제목 + 전원아이콘
export const ScheduleSystemHeaderNew = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '20px',
});

export const ScheduleSystemTitleNew = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flex: 1,
});

export const ScheduleSystemNameNew = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#000',
});

// 요일 버튼 (새 디자인) - 정사각형 대신 flex로
export const ScheduleDayGroupNew = styled(Box)({
  display: 'flex',
  gap: '8px',
  cursor: 'pointer',
  width: '100%',
});

export const ScheduleDayBtnNew = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  flex: 1,
  minWidth: 0,
  padding: '12px 8px',
  borderRadius: '12px',
  fontSize: '14px',
  fontWeight: 600,
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: isActive ? '#0F7F85' : '#DCDCDC',
  color: isActive ? '#fff' : '#333',
  transition: 'all 0.2s ease',
  '&:hover': {
    opacity: 0.8,
  },
}));

// 시간 선택기 (새 디자인)
export const ScheduleTimeGroupNew = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '100%',
});

export const ScheduleTimeBlockNew = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const ScheduleTimeSelectNew = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 6px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '4px',
  height: '32px',
  width: '100%',
  cursor: 'pointer',
  '& p': {
    fontSize: '13px',
    fontWeight: 500,
    color: '#404040',
    margin: 0,
    lineHeight: 1,
  },
});

export const ScheduleTimeSeparatorNew = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  width: '12px',
  textAlign: 'center',
  flexShrink: 0,
});

// 접기 버튼 (새 디자인) - 우측 정렬
export const ScheduleCollapseBtnV2 = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '4px',
  cursor: 'pointer',
  '& p': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#8C8C8C',
    margin: 0,
  },
  '&:hover': {
    '& p': {
      color: '#0F7F85',
    },
  },
});

// 설정 섹션 하단 영역
export const ScheduleSettingBottomSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  paddingTop: '16px',
});

// 조명 설정 그리드 (새 디자인) - 2열 그리드
export const ScheduleLightingGridNew = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '12px 16px',
  padding: '16px',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
});

export const ScheduleLightingItemNew = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  '& span': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#262626',
    whiteSpace: 'nowrap',
  },
});

// 추가하기 버튼 (새 디자인)
export const ScheduleAddBtnNew = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '6px',
  cursor: 'pointer',
  '& p': {
    fontSize: '16px',
    fontWeight: 500,
    color: '#A6A6A6',
  },
  '&:hover': {
    '& p': {
      color: '#0F7F85',
    },
    '& svg': {
      color: '#0F7F85',
    },
  },
});

export const ScheduleAddBtnNewText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  textAlign: 'center',
  flexShrink: 0,
});

// 스케줄 저장 버튼
export const ScheduleSaveBtn = styled(Box, {
  shouldForwardProp: prop => prop !== 'disabled',
})(({ disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 24px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  backgroundColor: disabled ? '#E0E0E0' : '#0F7F85',
  color: disabled ? '#999' : '#fff',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: disabled ? '#E0E0E0' : '#0D6A6F',
  },
}));

// 스케줄 삭제 버튼
export const ScheduleDeleteBtn = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 16px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: '#fff',
  color: '#FF4D4F',
  border: '1px solid #FF4D4F',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#FFF1F0',
  },
});

// 스케줄 버튼 그룹
export const ScheduleButtonGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  marginTop: '16px',
});

// 시간 선택 드롭다운 메뉴
export const ScheduleTimeDropdown = styled(Box)({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  maxHeight: '180px',
  overflowY: 'auto',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '4px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 100,
  marginTop: '2px',
});

export const ScheduleTimeDropdownItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  padding: '6px 8px',
  fontSize: '12px',
  fontWeight: isSelected ? 600 : 400,
  color: isSelected ? '#0F7F85' : '#404040',
  backgroundColor: isSelected ? '#E7F2F3' : '#fff',
  cursor: 'pointer',
  textAlign: 'center',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
}));

// 시간 선택 wrapper (드롭다운 위치용)
export const ScheduleTimeSelectWrapper = styled(Box)({
  position: 'relative',
  display: 'flex',
  flex: 1,
});

// ============================================
// 총관리자 IoT 연동 설정 패널
// ============================================
export const IotSetupPanel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
  padding: '12px 16px',
  marginBottom: '24px',
  borderRadius: '8px',
  border: '1px solid #0F7F85',
  backgroundColor: '#F2FAFA',
});

export const IotSetupLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#0F7F85',
});

export const IotSetupInfo = styled(Typography)({
  fontSize: '14px',
  color: '#333',
});

export const IotSetupSelect = styled(Select)({
  minWidth: '220px',
  height: '36px',
  fontSize: '14px',
  backgroundColor: '#fff',
});

export const IotSetupButton = styled(Button)({
  height: '36px',
  padding: '0 14px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#fff',
  backgroundColor: '#0F7F85',
  '&:hover': { backgroundColor: '#0C6A6F' },
  '&.Mui-disabled': { backgroundColor: '#B8DADB', color: '#fff' },
});

export const IotSetupDangerButton = styled(IotSetupButton)({
  color: '#D32F2F',
  backgroundColor: '#fff',
  border: '1px solid #D32F2F',
  '&:hover': { backgroundColor: '#FDECEC' },
  '&.Mui-disabled': { backgroundColor: '#fff', color: '#E8A0A0', borderColor: '#E8A0A0' },
});

// ============================================
// 룸 카드 2×2 타일 (일시개방 / 조명 / 냉난방 / 전광판)
// ============================================
// 카드가 늘어나면 아래로 쌓지 않고 가로로 넘긴다. overflow 가 테두리를 자르지 않게 안쪽 여백 + 바깥 여백 상쇄
export const RoomTileRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  width: 'calc(100% + 8px)',
  margin: '-4px -4px -6px',
  padding: '4px 4px 6px',
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  '&::-webkit-scrollbar': { height: '4px' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#E0E0E0', borderRadius: '2px' },
  // 3장 폭이 기본, 패널이 좁으면 최소 폭(136px)을 지키고 가로로 넘긴다
  '& > *': { flex: '0 0 auto !important', width: 'calc((100% - 8px - 16px) / 3)', minWidth: '136px', maxWidth: '200px', scrollSnapAlign: 'start' },
});

// 룸 카드 오른쪽 상단 카테고리 추가 버튼
export const RoomAddButton = styled(Button)({
  minWidth: 0,
  height: '28px',
  padding: '0 10px',
  borderRadius: '14px',
  border: '1px solid #BFE3E6',
  backgroundColor: '#fff',
  color: '#0F7F85',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { backgroundColor: '#F2FAFA', borderColor: '#0F7F85' },
  '&.Mui-disabled': { opacity: 0.5 },
});

export const RoomTile = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  minWidth: 0,
  height: '48px',
  padding: '0 12px',
  borderRadius: '8px',
  textTransform: 'none',
  backgroundColor: isActive ? '#F2FAFA' : '#fff',
  border: isActive ? '1px solid #0F7F85' : '1px solid #E0E0E0',
  transition: 'all 0.2s ease',
  '&:hover': { backgroundColor: isActive ? '#E7F2F3' : '#FAFAFA', borderColor: '#0F7F85' },
  '&.Mui-disabled': { opacity: 0.55, border: '1px solid #E0E0E0' },
}));

export const RoomTileLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
});

export const RoomTileLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: isActive ? '#0F7F85' : '#666',
  whiteSpace: 'nowrap',
}));

export const RoomTileStatus = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: isActive ? '#4AB3BC' : '#999',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

// ============================================
// 우측 패널 — 출입문 카드 세로 구성 / 전원 그룹 / 스케줄·예약 탭
// ============================================
export const DoorCardBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
});

export const DoorCardTop = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  flexWrap: 'wrap',
});

export const DoorStateText = styled(Typography)({
  fontSize: '13px',
  color: '#0F7F85',
});

// 출입문 카드 왼쪽: 제목 + 항시열림/자동잠김 토글
export const DoorCardLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

// 도어 인증 가능 시간: 아이콘 · 라벨 · 시간 · [수정] 한 덩어리
export const DoorOpeningInline = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 10px',
  borderRadius: '8px',
  backgroundColor: '#F7F7F7',
});

export const DoorOpeningLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#4D4D4D',
  whiteSpace: 'nowrap',
});

export const DoorOpeningTimes = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isSet',
})(({ isSet }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: isSet ? '#333' : '#999',
  fontVariantNumeric: 'tabular-nums',
}));

export const SmallOutlineButton = styled(Button)({
  minWidth: 0,
  height: '30px',
  padding: '0 10px',
  borderRadius: '6px',
  border: '1px solid #4AB3BC',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  backgroundColor: '#fff',
  '&.Mui-disabled': { opacity: 0.5 },
});

export const PowerGroup = styled(Box)({
  marginBottom: '16px',
  '&:last-of-type': { marginBottom: 0 },
});

export const PowerGroupHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

export const PowerGroupTitle = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#808080',
});

export const DetailTabBar = styled(Box)({
  display: 'flex',
  height: '40px',
  marginBottom: '16px',
  backgroundColor: '#F2F2F2',
  borderRadius: '12px',
});

export const DetailTabButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  flex: 1,
  height: '40px',
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '15px',
  fontWeight: 600,
  color: isActive ? '#333' : '#666',
  backgroundColor: isActive ? '#fff' : 'transparent',
  border: isActive ? '2px solid #F2F2F2' : '2px solid transparent',
  '&:hover': { backgroundColor: isActive ? '#fff' : '#EAEAEA' },
}));
